import LegalDocumentLayout, { LegalSection } from '../components/LegalDocumentLayout'

const EFFECTIVE_DATE = 'February 11, 2026'
const LAST_UPDATED = 'February 11, 2026'

const sections: LegalSection[] = [
  {
    id: 'acceptance',
    title: 'Acceptance and Eligibility',
    content: (
      <>
        <p>These Terms of Use govern your access to and use of Asterode. By creating an account or using the app, you agree to these Terms.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>You must be at least 18 years old.</li>
          <li>You must have legal capacity to enter into binding agreements.</li>
          <li>You are responsible for compliance with applicable laws in your region.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'service-nature',
    title: 'Service Nature and Availability',
    content: (
      <>
        <p>
          Asterode is an evolving product and may change over time. Features may be added, removed, modified, or paused as part of continuous
          development.
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Availability is not guaranteed.</li>
          <li>Some functionality may be experimental.</li>
          <li>Support is provided on a best-effort basis.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Accounts and Security',
    content: (
      <>
        <p>You are responsible for activities that occur under your account and for maintaining credential security.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Provide accurate registration information.</li>
          <li>Keep login credentials confidential.</li>
          <li>Notify support promptly in case of unauthorized access.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'billing',
    title: 'Subscriptions and Billing',
    content: (
      <>
        <p>Asterode may offer free and paid plans. Pricing, plan limits, and feature allocations can change over time.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Billing is handled through third-party processors and app stores.</li>
          <li>Subscriptions renew based on the billing cycle unless canceled.</li>
          <li>Refund handling follows platform policies and applicable law.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable Use',
    content: (
      <>
        <p>You agree not to misuse Asterode or use it in ways that harm users, systems, or third parties.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>No illegal activities, abuse, harassment, or rights violations.</li>
          <li>No attempts to disrupt, probe, reverse engineer, or exploit the service.</li>
          <li>No unauthorized automation, scraping, or account sharing.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'ai-limitations',
    title: 'AI Output Limitations',
    content: (
      <>
        <p>
          AI-generated outputs may be inaccurate, incomplete, outdated, or unsuitable for your use case. You are responsible for evaluating and
          validating model responses before relying on them.
        </p>
        <p>
          Asterode does not provide legal, medical, financial, or professional advice. Seek qualified professionals for high-stakes decisions.
        </p>
      </>
    ),
  },
  {
    id: 'data-and-privacy',
    title: 'Data and Privacy',
    content: (
      <>
        <p>
          Data handling practices are described in our{' '}
          <a href="/app-privacy" className="font-semibold text-ink-500 hover:text-ink-700">
            Privacy Policy
          </a>
          . By using Asterode, you acknowledge that prompts and content may be processed by third-party AI providers as needed to serve requests.
        </p>
      </>
    ),
  },
  {
    id: 'ai-providers',
    title: 'Third-Party AI Providers',
    content: (
      <>
        <p>Depending on which features you use, your data is transmitted to external providers as follows.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>When you send messages (including with attachments), your data is transmitted to external AI providers (OpenAI, Anthropic, Google, Meta, etc., via OpenRouter or directly).</li>
          <li>When you use voice input, your voice data is transmitted to an external transcription service, currently OpenAI.</li>
          <li>When you use web search, your search query is transmitted to an external web search service, currently EXA.</li>
          <li>Each provider has their own terms and policies. By using Asterode, you acknowledge that your conversations will be processed by these third parties.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    content: (
      <>
        <p>
          Asterode and its associated software, design, and branding are owned by Istvan Buzas (Stef Buzas), except for third-party technologies
          used under their respective licenses.
        </p>
        <p>You retain ownership of content you provide, subject to rights required to operate and improve the service.</p>
      </>
    ),
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers and Liability Limits',
    content: (
      <>
        <p>
          Asterode is provided on an "as is" and "as available" basis without warranties of uninterrupted service, perfect accuracy, or guaranteed
          fitness for a specific purpose.
        </p>
        <p>
          To the extent allowed by law, Asterode and its operator are not liable for indirect, incidental, consequential, or special damages
          resulting from use of the service.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes and Termination',
    content: (
      <>
        <p>We may update these Terms and may suspend or terminate accounts when necessary for legal, security, or policy reasons.</p>
        <p>Continued use of Asterode after updates indicates acceptance of revised Terms.</p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    content: (
      <>
        <p>Questions about these Terms can be sent to:</p>
        <p>
          <a href="mailto:support@asterode.ai" className="font-semibold text-ink-500 hover:text-ink-700">
            support@asterode.ai
          </a>
        </p>
      </>
    ),
  },
]

const Terms = () => {
  return (
    <LegalDocumentLayout
      title="Terms of Use for Asterode"
      subtitle="These terms define the conditions for using Asterode and explain service boundaries, responsibilities, and legal protections."
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={LAST_UPDATED}
      introCallout={
        <p>
          Important: Asterode includes AI-generated outputs and third-party AI providers. Review limitations carefully before relying on responses
          for critical decisions.
        </p>
      }
      sections={sections}
    />
  )
}

export default Terms


